import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import {
    Card,
    InputAdornment,
    List,
    ListItem,
    Skeleton,
    Switch,
    FormControlLabel,
    TextField,
    Dialog,
    IconButton,
} from "@material-ui/core";
import styled from "@emotion/styled";
import SearchIcon from "@material-ui/icons/Search";
import { useTranslation } from "react-i18next";
import { Coordinates, toCoordinates } from "@optimusride/client-library/@types/grpc/common";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { Station } from "@optimusride/client-library/@types/grpc/station";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
    SPACING_1,
    SPACING_8,
    SPACING_12,
    SPACING_16,
    SPACING_20,
    SPACING_28,
    SPACING_32,
    SPACING_48,
    SPACING_80,
    SPACING_24,
} from "../../common/styles/spacing";
import { FONT_0_75 } from "../../common/styles/fonts";
import { H5, P } from "../../common/styles/typography";
import { BLACK, GRAY_320 } from "../../common/styles/colors";
import StationListItem from "./StationListItem";
import { createStation, updateStation, StationWithLetterID } from "../../User/userSlice";
import { getAddressString } from "../utils";
import StationEditorForm from "./StationEditorForm";
import { useModal } from "../../common/hooks/useModal";
import { apis } from "../../common/utils/auth";
import { addAlert } from "../../Alerts/alertsSlice";

const StyledStationCard = styled(Card)`
    position: relative;

    height: 95%;
    
    margin-top: ${SPACING_8};

    border-radius: ${SPACING_8};
`;

const StyledSearchBox = styled(TextField)`
    padding: ${SPACING_20} ${SPACING_20} 0 ${SPACING_20};

    .MuiOutlinedInput-root {
        border-radius: ${SPACING_32};
    }

    .MuiOutlinedInput-input {
        padding-top: ${SPACING_8};
        padding-bottom: ${SPACING_8};

        &::placeholder {
            font-size: ${FONT_0_75};
        }
    }
`;

const StyledSkeletonSearchBox = styled(Skeleton)`
    padding: ${SPACING_32} ${SPACING_16} ${SPACING_8};
    margin-left: ${SPACING_20};
    margin-top: ${SPACING_8};
    border-radius: ${SPACING_28};

    & .MuiFormControl-root {
        padding: 0;
        height: 0;
    }
`;

const StyledSkeletonListItem = styled(Skeleton)`
    margin: ${SPACING_8} ${SPACING_16} ${SPACING_12};
    max-width: 100%;
    max-height: ${SPACING_48};

    transform: none;
`;

const StyledList = styled(List)`
    margin-top: ${(props: { loading?: boolean }) => (!props.loading ? SPACING_12 : "0")};
    padding-top: ${(props: { loading?: boolean }) => (!props.loading ? SPACING_8 : "0")};

    height: 100%;

    overflow-y: scroll;
`;

const StyledListItem = styled(ListItem)`
    height: ${SPACING_80};
    align-items: flex-start;
    cursor: auto;
`;

const StyledHR = styled.hr`
    margin: 0;
    border-top: solid ${SPACING_1} ${GRAY_320};
    color: ${GRAY_320};

    &:last-child {
        margin-bottom: ${SPACING_80};
    }
`;

const StyledHeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledTitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const StyledAddStationButton = styled(IconButton)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${BLACK};

    border-radius: ${SPACING_24};

    svg {
        margin-right: ${SPACING_8};
    }


`;

const StartAdornment = () => (
    <InputAdornment position="start">
        <SearchIcon />
    </InputAdornment>
);

const parseStation = (station: StationWithLetterID) => ({
    name: station.nickname,
    address1: station.address1,
    address2: station.address2,
    city: station.city,
    state: station.state,
    zip: station.postalcode,
    lat: station.latLon.latitude,
    lng: station.latLon.longitude,
    disabled: station.disabled,
    linksFromList: station.linksFromList,
    linksToList: station.linksToList,
});

export default ({
    operatingArea,
    stations,
    isAdmin = false,
    onClick,
    className,
}: {
    operatingArea: number;
    stations?: StationWithLetterID[];
    isAdmin?: boolean;
    onClick: (center: Coordinates) => void;
    className?: string;
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [showDisabled, setShowDisabled] = useState(false);
    const [selectedStation, setSelectedStation] = useState<null | number>(null);
    const [open, toggleOpen] = useModal();
    const dispatch = useDispatch();

    const searchBoxRef = useRef<HTMLInputElement>(null);

    const filteredStations = (stations || []).filter(({
        nickname,
        address1, address2, city, state, postalcode, country,
    }) => (
        nickname.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
        || getAddressString({
            address1,
            address2,
            city,
            state,
            postalcode,
            country,
        }).toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
    )).sort(
        ({ letterID: letterIDA }, { letterID: letterIDB }) => letterIDA?.localeCompare(letterIDB),
    );

    const filteredEnabledStations = filteredStations.filter(({ disabled }) => !disabled);
    const filteredDisabledStations = filteredStations.filter(({ disabled }) => disabled);

    const stationsToShow = [
        ...filteredEnabledStations,
        ...(showDisabled ? filteredDisabledStations : []),
    ];

    const listOfSourceDestinationStations = useMemo(() => (stations || [])
        .filter(({ disabled }) => !disabled)
        .reduce((prev, { id, nickname }) => (
            [...prev, [id, nickname] as [number, string]]
        ), [] as [number, string][])
        .sort(([, nameA], [, nameB]) => (
            nameA.toLocaleLowerCase().localeCompare(nameB.toLocaleLowerCase())
        )), [stations]);

    const selectedAndParsedStation = useMemo(() => (
        selectedStation !== null && stationsToShow[selectedStation]
            ? parseStation(stationsToShow[selectedStation])
            : {}
    ), [selectedStation]);

    useEffect(() => {
        if (searchBoxRef.current) {
            searchBoxRef.current.value = "";
        }
        setSearchTerm("");
    }, [stations]);

    const toggleStation = useCallback((index: number | null) => {
        setSelectedStation(index);
        toggleOpen();
    }, [setSelectedStation, toggleOpen]);

    const exitForm = useCallback(() => {
        toggleStation(null);
    }, [toggleStation]);

    const handleStationUpdate = useCallback(async (station: Partial<Station>) => {
        try {
            const disabledDate = station.disabled ? dayjs().format("YYYY-MM-DD") : undefined;

            if (selectedStation === null) {
                const newStation = await apis.station
                    .addStation(station as Station, operatingArea, disabledDate);
                const newStationObject = newStation?.toObject() || {};

                if (newStation) {
                    dispatch(createStation({
                        station: {
                            ...newStationObject,
                            ...(newStationObject?.address || {}),
                            postalcode: newStationObject?.address?.postalCode || "",
                            letterID: "",
                            latLon: toCoordinates(newStation.getLatLon()),
                            disabled: newStationObject.disabledDate !== "",
                            linksFromList: newStationObject.linksFromList?.filter((id) => id >= 0),
                            linksToList: newStationObject.linksToList?.filter((id) => id >= 0),
                        },
                        operatingArea,
                    }));
                }
            } else {
                const updatedStation = await apis.station
                    .updateStation(station as Station, operatingArea, disabledDate);

                const oldStationIndex = stations?.findIndex(
                    ({ id }) => id === updatedStation.getId(),
                );
                const oldStation = oldStationIndex !== undefined
                    ? stations?.[oldStationIndex]
                    : undefined;

                const updatedStationObject = updatedStation.toObject() || {};

                if (updatedStation) {
                    dispatch(updateStation({
                        station: {
                            letterID: "",
                            ...(oldStation || {}),
                            ...updatedStationObject,
                            ...(updatedStationObject?.address || {}),
                            postalcode: updatedStationObject?.address?.postalCode || "",
                            latLon: toCoordinates(updatedStation.getLatLon()),
                            disabled: updatedStationObject.disabledDate !== "",
                            linksFromList: updatedStationObject
                                .linksFromList?.filter((id) => id >= 0),
                            linksToList: updatedStationObject
                                .linksToList?.filter((id) => id >= 0),
                        },
                        oldStationIndex,
                        operatingArea,
                    }));
                }
            }

            toggleStation(null);
        } catch (e) {
            dispatch(addAlert({
                title: "Station Error",
                text: (e as Error).message,
                type: "error",
                ttl: 7000,
            }));
        }
    }, [toggleStation, selectedStation, operatingArea]);

    const Body = operatingArea === -1 || stations === undefined ? (
        <>
            <StyledSkeletonSearchBox>
                <StyledSearchBox />
            </StyledSkeletonSearchBox>
            <StyledList>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((value) => (
                    <React.Fragment key={`loading-${value}`}>
                        <StyledSkeletonListItem>
                            <StyledListItem button key={`loading${value}`} />
                        </StyledSkeletonListItem>
                        <StyledHR />
                    </React.Fragment>
                ))}
            </StyledList>
        </>
    ) : (
        <>
            <StyledHeaderContainer>
                <StyledSearchBox
                    placeholder={t("home.station.search")}
                    InputProps={{
                        startAdornment: <StartAdornment />,
                    }}
                    inputRef={searchBoxRef}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FormControlLabel
                    control={(
                        <Switch
                            value={showDisabled}
                            color="primary"
                            onChange={() => setShowDisabled((_showDisabled) => !_showDisabled)}
                        />
                    )}
                    label={t("home.station.showStations")}
                />

            </StyledHeaderContainer>
            <StyledList>
                {stationsToShow
                    .map(({
                        nickname, id, latLon, letterID,
                        address1, address2, city, state, postalcode, country,
                        disabled,
                    }, index) => (
                        <React.Fragment key={id}>
                            <StyledListItem button>
                                <StationListItem
                                    nickname={nickname}
                                    address={{
                                        address1,
                                        address2,
                                        city,
                                        state,
                                        postalcode,
                                        country,
                                    }}
                                    disabled={disabled}
                                    latLon={latLon}
                                    letterID={letterID}
                                    handleClick={() => latLon && onClick(latLon)}
                                    handleStationEdit={() => {
                                        toggleStation(index);
                                    }}
                                    isAdmin={isAdmin}
                                />
                            </StyledListItem>
                            <StyledHR />
                        </React.Fragment>
                    ))}
            </StyledList>
        </>
    );

    return (
        <div className={className}>
            <StyledTitleContainer>
                <H5>{t("home.station.title")}</H5>
                {isAdmin && (
                    <StyledAddStationButton onClick={toggleOpen}>
                        <AddCircleOutlineIcon />
                        <P>{t("home.station.addStation")}</P>
                    </StyledAddStationButton>
                )}
            </StyledTitleContainer>
            <StyledStationCard>
                {Body}
            </StyledStationCard>
            {open && isAdmin && (
                <Dialog open={open} maxWidth="md">
                    <StationEditorForm
                        station={selectedAndParsedStation}
                        stationID={selectedStation === null
                            ? null : stationsToShow[selectedStation]?.id}
                        listOfSourceDestinationStations={listOfSourceDestinationStations}
                        onUpdate={handleStationUpdate}
                        onExit={exitForm}
                        proceedText={
                            selectedStation === null
                                ? "home.stationEditor.create"
                                : "home.stationEditor.update"
                        }
                    />
                </Dialog>
            )}
        </div>
    );
};
